import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, logAnalyticsEvent } from '../firebase/config';
import { User } from '../types';
import { useTranslation } from 'react-i18next';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  updateUserState: (updates: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mapeia códigos de erro do Firebase Auth para chaves de tradução.
const authErrorKey = (code?: string): string => {
  switch (code) {
    case 'auth/invalid-email':
      return 'auth.errors.invalidEmail';
    case 'auth/user-disabled':
      return 'auth.errors.userDisabled';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'auth.errors.invalidCredential';
    case 'auth/too-many-requests':
      return 'auth.errors.tooManyRequests';
    case 'auth/network-request-failed':
      return 'auth.errors.network';
    case 'auth/operation-not-allowed':
      return 'auth.errors.operationNotAllowed';
    case 'auth/email-already-in-use':
      return 'auth.errors.emailInUse';
    case 'auth/weak-password':
      return 'auth.errors.weakPassword';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'auth.errors.popupClosed';
    default:
      return 'authError';
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const updateUserState = async (updates: Partial<User>) => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.id);
      await setDoc(userRef, {
        ...updates,
        updatedAt: new Date()
      }, { merge: true });

      setUser(prevUser => prevUser ? { ...prevUser, ...updates } : null);

      if (updates.photoURL && auth.currentUser) {
        await updateProfile(auth.currentUser, { photoURL: updates.photoURL });
      }
    } catch (err) {
      console.error('Error updating user state:', err);
      throw err;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          const userData: User = {
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || '',
            isAdmin: userDoc?.exists() ? userDoc.data().isAdmin === true : false,
            photoURL: firebaseUser.photoURL || undefined,
            phone: userDoc?.exists() ? userDoc.data().phone : undefined,
            city: userDoc?.exists() ? userDoc.data().city : undefined
          };
          
          if (!userDoc?.exists()) {
            await setDoc(userDocRef, {
              email: userData.email,
              name: userData.name,
              photoURL: userData.photoURL,
              isAdmin: false,
              createdAt: new Date()
            });
          }
          
          setUser(userData);
          setIsAdmin(userData.isAdmin || false);
        } catch (err) {
          console.error('Error in auth state change:', err);
          setUser(null);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const register = async (email: string, password: string, name: string) => {
    setError(null);
    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: name });
      }
      
      try {
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email,
          name,
          isAdmin: false,
          photoURL: userCredential.user.photoURL,
          createdAt: new Date()
        });

        logAnalyticsEvent('sign_up', {
          method: 'email'
        });
      } catch (err) {
        console.warn('Failed to create user document:', err);
      }

    } catch (err) {
      const code = (err as { code?: string })?.code;
      console.error('Register error:', code, err);
      setError(t(authErrorKey(code)));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setError(null);
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);

      logAnalyticsEvent('login', {
        method: 'email'
      });
    } catch (err) {
      const code = (err as { code?: string })?.code;
      console.error('Login error:', code, err);
      setError(t(authErrorKey(code)));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setError(null);
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      try {
        const userDocRef = doc(db, 'users', result.user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          await setDoc(userDocRef, {
            email: result.user.email,
            name: result.user.displayName,
            isAdmin: false,
            photoURL: result.user.photoURL,
            createdAt: new Date()
          });

          logAnalyticsEvent('sign_up', {
            method: 'google'
          });
        } else {
          logAnalyticsEvent('login', {
            method: 'google'
          });
        }
      } catch (err) {
        console.warn('Failed to check/create user document:', err);
      }
    } catch (err) {
      const code = (err as { code?: string })?.code;
      console.error('Google login error:', code, err);
      setError(t(authErrorKey(code)));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setError(null);
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      
      logAnalyticsEvent('password_reset', {
        method: 'email'
      });
    } catch (err) {
      setError(t('auth.resetPassword.error'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin,
        login,
        register,
        loginWithGoogle,
        logout,
        resetPassword,
        loading,
        error,
        updateUserState
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};